/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IComponent, IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import React from 'react';

/**
 * Interface for quick order table header resources.
 */
export interface IQuickOrderTableHeaderResources {
    quickOrderProductNumberText: string;
    quickOrderProductText: string;
    quickOrderUnitPriceText: string;
    quickOrderUnitOfMeasureText: string;
    quickOrderQuantityText: string;
    quickOrderTotalText: string;
    quickOrderActionsText: string;
}

/**
 * Interface for quick order table props.
 */
export interface IQuickOrderTableHeaderProps extends IComponentProps {
    context: ICoreContext;
    className: string;

    isMobile: boolean;
    resources: IQuickOrderTableHeaderResources;
}

/**
 * Interface for quick order table header component.
 */
export interface IQuickOrderTableHeaderComponent extends IComponent<IQuickOrderTableHeaderProps> {
}

/**
 * Get the quick order header cells for desktop.
 * @param props -- The quick order table header props.
 * @returns The list of cells for quick order header component in desktop version.
 */
const QuickOrderDesktopHeaderCellsComponent: React.FC<IQuickOrderTableHeaderProps> = (props: IQuickOrderTableHeaderProps) => {
    const { resources } = props;
    const columnWithNumberClassName = `${props.className}__row__column-with-number`;
    return (
        <>
            <th>
                <span className={classnames(`${props.className}__emptyColumn`)} />
            </th>
            <th>
                {resources.quickOrderProductNumberText}
            </th>
            <th>
                {resources.quickOrderProductText}
            </th>
            <th className={columnWithNumberClassName}>
                {resources.quickOrderUnitPriceText}
            </th>
            <th>
                {resources.quickOrderUnitOfMeasureText}
            </th>
            <th className={columnWithNumberClassName}>
                {resources.quickOrderQuantityText}
            </th>
            <th className={columnWithNumberClassName}>
                {resources.quickOrderTotalText}
            </th>
            <th>
                {resources.quickOrderActionsText}
            </th>
        </>
    );
};

/**
 * Get the quick order header cells for mobile.
 * @param props -- The quick order table header props.
 * @returns The quick order header component in desktop version.
 */
const QuickOrderMobileHeaderCellsComponent: React.FC<IQuickOrderTableHeaderProps> = (props: IQuickOrderTableHeaderProps) => {
    const { resources } = props;
    return (
        <>
            <th className={classnames(`${props.className}__emptyColumn`)} />
            <th>
                {resources.quickOrderProductNumberText}
            </th>
            <th />
        </>
    );
};

/**
 * Quick order table header component.
 * @param props - Configuration of the component.
 * @returns React node with the header.
 */
export const QuickOrderTableHeaderComponent: React.FC<IQuickOrderTableHeaderProps> = (props: IQuickOrderTableHeaderProps) => {
    const rowClassName = `${props.className}__row`;

    let headerCells: JSX.Element;
    if (props.isMobile) {
        headerCells = (<QuickOrderMobileHeaderCellsComponent {...props} />);
    } else {
        headerCells = (<QuickOrderDesktopHeaderCellsComponent {...props} />);
    }

    return (
        <thead className={props.className}>
            <tr className={rowClassName}>
                {headerCells}
            </tr>
        </thead>
    );
};
